// Página de ruta para los códigos del Grupo 1 (Módulo H) — lee :codigo y
// subject/?encounter= de la URL, mismo patrón que NuevaConsultaPage.tsx.
// Aplica el gate de especialidad (punto 6 del pedido original) antes de
// mostrar el formulario — chequeo adicional a lo que ya filtra
// SelectorFormularios (que solo gatea la LISTA, no el acceso directo por
// URL).
import type { Patient, Reference } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { codigosFormularioDeEspecialidad } from '@/lib/catalogoEspecialidades';
import { useEncounterValidado } from '@/lib/useEncounterValidado';
import { useEspecialidadDelPracticante } from '@/lib/useEspecialidadDelPracticante';
import { MotorFormulario } from './MotorFormulario';

export function MotorFormularioPage({ codigoFijo }: { readonly codigoFijo?: string } = {}): JSX.Element {
  const { t } = useTranslation();
  const params = useParams() as { id: string; codigo?: string };
  const id = params.id;
  /** Las rutas estáticas (p. ej. 002/003 al reabrir una respuesta histórica) pasan el código; las dinámicas lo leen de `:codigo`. */
  const codigo = codigoFijo ?? params.codigo ?? '';
  const [searchParams] = useSearchParams();
  const encounterParam = searchParams.get('encounter');
  const respuestaId = searchParams.get('respuestaId') ?? undefined;
  const especialidad = useEspecialidadDelPracticante();

  // `useQuestionnaireForm` reinicia su estado interno cuando `subject`/
  // `encounter` cambian de referencia — sin memoizar, un objeto literal
  // nuevo en cada render (causado por CUALQUIER re-render, incluido el que
  // dispara la propia respuesta al escribir) borraba lo que se acababa de
  // tipear. Bug real encontrado en verificación en vivo 2026-08-31.
  const subject = useMemo<Reference<Patient>>(() => ({ reference: `Patient/${id}` }), [id]);
  const encounterValidado = useEncounterValidado(subject, encounterParam);

  if (especialidad.cargando || encounterValidado.cargando) {
    return <p className="text-sm text-hce-muted">{t('formulario.cargando')}</p>;
  }

  // Fail-closed cuando la especialidad no resolvió — ANTES no restringía
  // ("cuenta legacy sin especialidad resuelta, mismo criterio que
  // gruposHabilitados.ts") pero eso saltaba el gate por completo para
  // cualquier código. Bug real confirmado en logs de producción 2026-09-03
  // (ver el comentario largo en FormularioDiscretoPage.tsx) — investigado
  // antes de aplicar esto: no hay ningún cliente real hoy sin
  // PractitionerRole. `gruposHabilitados.ts` (qué PESTAÑA de grupo se
  // muestra, no qué formulario se puede abrir) sigue sin restringir a
  // propósito — es una decisión de UI distinta, documentada aparte ahí.
  if (!especialidad.codigo || !codigosFormularioDeEspecialidad(especialidad.codigo).includes(codigo)) {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formulario.fueraDeEspecialidadTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.fueraDeEspecialidadDetalle')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {encounterValidado.invalido && (
        <Alert variant="orange">
          <AlertTitle>{t('formulario.encounterInvalidoTitulo')}</AlertTitle>
          <AlertDescription>{t('formulario.encounterInvalidoDetalle')}</AlertDescription>
        </Alert>
      )}
      <MotorFormulario codigo={codigo} subject={subject} encounter={encounterValidado.encounter} respuestaId={respuestaId} />
    </div>
  );
}
